import { mapActions, mapGetters, mapState } from 'vuex'

import CONSTANTS from '@/constants'

const { PLANS, PREMIUM_PLANS, LIVE_VIRTUAL_TRAININGS, BLACK_FRIDAY_PRODUCT_BUNDLES } = CONSTANTS
const { INE_BLACK_FRIDAY_BUNDLE_THREE } = BLACK_FRIDAY_PRODUCT_BUNDLES

export const priceDetailUpdates = {
  data() {
    return {
      currencySymbol: '$',
      isPromoAvailable: false,
      currentCouponInfo: {},
      couponError: null,
      currentDiscount: 0,
      planCodes: Object.values(CONSTANTS.PLANS),
    }
  },
  computed: {
    ...mapState({
      recurlyPricing: state => state.subscriptions.recurlyPricing,
      couponDiscount: state => state.subscriptions.checkoutCouponDiscount,
      discount: state => state.subscriptions.checkoutDiscount,
      subtotal: state => state.subscriptions.checkoutSubtotal,
      taxes: state => state.subscriptions.checkoutTaxes,
      total: state => state.subscriptions.checkoutTotal,
      customCoupon: state => state.shoppingCart.coupon,
      dataLayer: state => state.events.dataLayer,
      paymentEntries: state => state.subscriptions.paymentEntries,
      shoppingCart: state => state.shoppingCart.shoppingCart,
      showPromotionSavings: state => state.shoppingCart.showPromotionSavings,
      userProfile: state => state.auth.userProfile,
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    ...mapGetters({
      selectedPlan: 'shoppingCart/selectedPlan',
      isBlackFridayActive: 'app/isBlackFridayActive',
      isLeapYearPromoActive: 'app/isLeapYearPromoActive',
      isPromotionActive: 'shoppingCart/getIsPromotionActive',
      plans: 'products/plansByTerm',
      isBusinessUser: 'business/isBusinessUser',
      isBusinessPlanExpired: 'business/isBusinessPlanExpired',
    }),
    coupon: {
      set(value) {
        this.$store.dispatch('shoppingCart/updateCoupon', value)
      },
      get() {
        return this.customCoupon
      },
    },
    currentCoupon: {
      get() {
        return this.currentCouponInfo
      },
      set(newValue) {
        this.currentCouponInfo = newValue
      },
    },
    selectedPlanCode() {
      return this.selectedPlan?.merchant_id ?? ''
    },
    skillDivePlan() {
      return this.plans.yearly.find(plan => plan.merchant_id === PLANS.SKILL_DIVE_ANNUAL)
    },
    isElegibleUser() {
      if (!this.isAuthenticated) return false
      const selectedUser = this.usersCurrentSubscriptions.find(subs =>
        [PLANS.ANNUAL, ...Object.values(PREMIUM_PLANS)].includes(subs.merchant_id || subs.plan_code)
      )
      return !!selectedUser && !(this.isBusinessUser || this.isBusinessPlanExpired)
    },
  },
  methods: {
    ...mapActions({
      updateRecurlyPricing: 'subscriptions/updateRecurlyPricing',
    }),
    addCoupon() {
      this.couponError = null
      if (this.coupon.length > 0) {
        this.handleCouponInformation()
      }
    },
    removeCoupon() {
      this.coupon = ''
      this.currentCouponInfo = {}
      this.couponError = null
      this.$store.dispatch('subscriptions/updateCheckoutCouponDiscount', (0).toFixed(2))
      this.handlePricing()
    },
    async handleCouponInformation() {
      const couponHasError = () => {
        this.couponError = true
        this.couponSpinner = false
        this.$store.dispatch('subscriptions/updateCheckoutCouponDiscount', (0).toFixed(2))
      }
      try {
        if (this.showPromotionSavings) throw couponHasError()
        this.couponSpinner = true
        const couponInformation = await this.$store.dispatch(
          'subscriptions/getCouponInformation',
          this.customCoupon
        )

        if (couponInformation) {
          this.currentCouponInfo = couponInformation
          const couponItems = [
            ...couponInformation.items.map(i => i.code),
            ...couponInformation.plans.map(i => i.code),
          ]

          let couponAvailableForDiscount = false
          let totalDiscountToApply = 0

          if (couponInformation.discount && couponInformation.discount.percent) {
            this.shoppingCart.forEach(item => {
              if (couponItems.includes(item.merchant_id)) {
                couponAvailableForDiscount = true
                const { percent } = couponInformation.discount
                const currentItemPrice = item.user_receives_discount
                  ? item.discounted_price_cents
                  : item.price_cents
                totalDiscountToApply += (percent * (currentItemPrice / 100)) / 100
              }
            })
          } else if (couponInformation.discount && couponInformation.discount.amount) {
            const { amount } = couponInformation.discount
            if (this.shoppingCart.some(item => couponItems.includes(item.merchant_id))) {
              couponAvailableForDiscount = true
              totalDiscountToApply = amount
            }
          }

          if (couponAvailableForDiscount) {
            this.$store.dispatch(
              'subscriptions/updateCheckoutCouponDiscount',
              totalDiscountToApply.toFixed(2)
            )
            this.couponError = false
            this.couponSpinner = false
          } else {
            couponHasError()
          }
        } else {
          couponHasError()
        }
      } catch (error) {
        if (error) this.handleError(error)
        couponHasError()
      }
      this.handlePricing()
    },
    handlePricing() {
      let taxes = 0
      if (!!this.recurlyPricing && !!this.recurlyPricing.price) {
        const {
          currency: { symbol },
          now: { taxes: taxesRecurly },
        } = this.recurlyPricing.price

        if (symbol && symbol.length) {
          this.currencySymbol = symbol
          taxes = Number(taxesRecurly)
        }
      }
      let discount = 0
      let total = 0
      taxes = taxes ? taxes * 100 : taxes
      let subtotal = 0

      const cartItemsCodes = []
      this.shoppingCart.forEach(item => {
        cartItemsCodes.push(item.merchant_id)
        // LVT price correction
        if (item.user_receives_discount && !(this.isBusinessUser || this.isBusinessPlanExpired)) {
          if (Object.values(LIVE_VIRTUAL_TRAININGS).includes(item.merchant_id)) {
            subtotal = subtotal - item.price_cents + item.discounted_price_cents
          }
        }
        subtotal += item.price_cents
      })

      if (this.isPromotionActive && this.showPromotionSavings) {
        discount = this.promoProductBundlePricing(discount)
      }

      // if this.couponDiscount !== 0 is true, means that the user has enter a valid coupon, so we add that value to discount
      // discount might have some previous value from this.promoProductBundlePricing execution
      if (Number(this.couponDiscount) !== 0) {
        discount += Number(this.couponDiscount) * 100
      }

      if (taxes) {
        taxes /= subtotal
        taxes *= subtotal - discount
      }
      total = subtotal + taxes - discount
      subtotal = (subtotal / 100).toFixed(2)
      discount = (discount / 100).toFixed(2)
      taxes = (taxes / 100).toFixed(2)
      total = (total / 100).toFixed(2)

      this.currentDiscount = discount
      this.$store.dispatch('subscriptions/updateCheckoutDiscount', discount)
      this.$store.dispatch('subscriptions/updateCheckoutTaxes', taxes)
      this.$store.dispatch('subscriptions/updateCheckoutSubtotal', subtotal)
      this.$store.dispatch('subscriptions/updateCheckoutTotal', total)
    },
    calculatePricingForPlans(item) {
      const isOnlyPlanLeft = this.shoppingCart.filter(itemCart =>
        this.planCodes.includes(itemCart.merchant_id)
      )
      if (item.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL) {
        if (!item.recurlyItem || item.recurlySDLACounter !== item.addOnsOnCart.length) {
          item.recurlySDLACounter = item.addOnsOnCart.length
          this.createSubscription(item)
            .catch(error => this.handleError(error))
            .done()
        }
      } else {
        if (!item.recurlyItem) {
          this.createSubscription(item)
            .catch(error => this.handleError(error))
            .done()
        }
        if (!item.recurlyItem && isOnlyPlanLeft.length === 1) {
          this.createSubscription(item)
            .catch(error => this.handleError(error))
            .done()
        }
      }
    },
    calculatePricing() {
      if (this.shoppingCart && this.shoppingCart.length) {
        this.shoppingCart.forEach(item => {
          if (item.merchant_id && this.planCodes.includes(item.merchant_id)) {
            this.calculatePricingForPlans(item)
          } else {
            if (
              !this.recurlyPricing.items.adjustments.find(
                recurlyItem => recurlyItem.id === item.merchant_id
              )
            ) {
              this.addSingleProduct(item)
                .catch(error => this.handleError(error))
                .done()
            }
          }
        })
        this.$store.dispatch('subscriptions/updateRecurlyPricing', this.recurlyPricing)
      }
    },
    createSubscription(plan) {
      const { country, zip: postal_code } = this.paymentEntries
      let subscription
      if (plan.merchant_id === INE_BLACK_FRIDAY_BUNDLE_THREE) {
        subscription = window.recurly.Pricing.Subscription().plan(PLANS.ANNUAL)
      } else if (plan.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL) {
        subscription = window.recurly.Pricing.Subscription().plan(plan.merchant_id)
        plan.addOnsOnCart.forEach(SDAddOn => subscription.addon(SDAddOn.merchant_id))
      } else {
        subscription = window.recurly.Pricing.Subscription().plan(plan.merchant_id)
      }
      plan.recurlyItem = true
      return this.recurlyPricing
        .subscription(subscription.catch(error => this.handleError(error)).done())
        .address({ country, postal_code })
    },
    addSingleProduct(item) {
      return this.recurlyPricing.adjustment({
        id: item.merchant_id,
        itemCode: item.merchant_id,
        quantity: 1,
        taxExempt: false,
      })
    },
    handleError(error) {
      console.error('handleError', error)
    },
    // function to calculate discount and total when special promotions are active
    // special promotions like BlackFriday, CyberMonday, LeapYear or JuneSavings
    promoProductBundlePricing(discountVal = 0) {
      let discount = discountVal * 100 || 0
      const cartItems = []
      this.shoppingCart.forEach(item => {
        cartItems.push(item.merchant_id || item.plan_code)
        if (this.isPromotionActive && this.showPromotionSavings) {
          if (item.slug === INE_BLACK_FRIDAY_BUNDLE_THREE || item.merchant_id === PLANS.ANNUAL) {
            const fundamentalsAnnual = this.availablePlans.find(
              plan => plan.merchant_id && plan.merchant_id === PLANS.ANNUAL
            )
            discount += fundamentalsAnnual.price_cents - fundamentalsAnnual.discounted_price_cents
          } else {
            if (item.discounted_price_cents && item.user_receives_discount) {
              discount += item.price_cents - (item.discounted_price_cents || 0)
            }
          }
        }
        if (
          Object.values(LIVE_VIRTUAL_TRAININGS).includes(item.merchant_id) &&
          item.user_receives_discount
        ) {
          lvtOnCartPrice = item.price_cents - item.discounted_price_cents
          discount -= lvtOnCartPrice
        }
      })
      return discount
    },
    shopFooterTotalAmount() {
      let totalAmount = 0
      const cartItems = []
      this.shoppingCart.forEach(item => {
        cartItems.push(item.merchant_id || item.plan_code)
        if (item.user_receives_discount && !(this.isBusinessUser || this.isBusinessPlanExpired)) {
          totalAmount += item.discounted_price_cents
        } else {
          totalAmount += item.price_cents
        }
      })

      return (totalAmount / 100).toFixed(2)
    },
  },
}
